import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { PageHero, SectionLabel } from "@/components/Bits";
import Seo from "@/components/Seo";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { DIFFERENTIATORS, IMAGES, STATS } from "@/lib/site";

export default function WhyCCA() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div data-testid="why-cca-page">
      <PageHero
        eyebrow="Why CCA"
        title="A trusted partner for disciplined pipeline development."
        intro="Agency owners choose CCA because we replace unpredictable prospecting with an engineered system — measured, accountable, and built exclusively for commercial insurance."
      />

      <section className="border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <SectionLabel num="/">Six reasons owners partner with us</SectionLabel>
          </Reveal>
          <div className="mt-12 border-t border-line">
            {DIFFERENTIATORS.map((d, i) => (
              <Reveal key={d.n} delay={i * 0.04}>
                <div className="group grid grid-cols-1 gap-4 border-b border-line py-8 transition-colors hover:bg-white md:grid-cols-12 md:gap-8 md:py-10">
                  <div className="font-mono text-sm text-clay md:col-span-1">{d.n}</div>
                  <h3 className="font-display text-2xl font-medium tracking-tight text-ink md:col-span-4 md:text-3xl">
                    {d.title}
                  </h3>
                  <p className="leading-relaxed text-subtext md:col-span-6 md:col-start-7">{d.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section ref={ref} className="relative overflow-hidden bg-ink py-28 text-paper md:py-40">
        <motion.img
          style={{ y }}
          src={IMAGES.glassHall}
          alt="Structured glass corridor"
          className="absolute inset-0 h-[120%] w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <h2 className="max-w-3xl font-display text-3xl font-medium leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              Add to capacity of an internal prospecting department — without the upfront cost or risk.
            </h2>
          </Reveal>
          <RevealGroup className="mt-16 grid grid-cols-2 gap-8 border-t border-inkline pt-10 md:grid-cols-4">
            {STATS.map((s) => (
              <RevealItem key={s.label}>
                <p className="font-display text-4xl font-medium tracking-tight md:text-5xl">{s.value}</p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.12em] text-invtext">
                  {s.label}
                </p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

    </div>
  );
}
